import React, { useRef } from "react";
import { Alert, Card } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/esm/Col";
import Dropdown from "react-bootstrap/esm/Dropdown";
import Row from "react-bootstrap/esm/Row";
import Table from "react-bootstrap/Table";
import ReactDOM from "react-dom";
import { faColumns, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingOverlay } from "../../common/Loading/LoadingOverlay";
import { ColumnRender, ColumnRenderAlignment, IColumn, IPagedListAction, SortOrder } from "../Declarations";
import { StickyScrollBar } from "../StickyScrollBar";
import { ValueRenderer } from "../ValueRenderer";

interface PagedListTableProps {
    visibleColumns: IColumn[];
    results: any[];
    hasLoadError: boolean;
    loadErrorMessage: string | null;
    hasResults: boolean;
    total: number;
    isBusy: boolean;
    tableWidth: number;
    sortBy: string;
    sortOrder: SortOrder;
    primaryActions: IPagedListAction[];
    secondaryActions: IPagedListAction[];
    keyColumn: string;
    avatarUrl?: string;
    onSortByClicked: (member: string) => void;
    onSelectColumnsClicked: () => void;
    onScroll: (value: number) => void;
    getRowClassName: (item: any) => string;
}

export const PagedListTable: React.FC<PagedListTableProps> = ({
    visibleColumns,
    results,
    hasLoadError,
    loadErrorMessage,
    hasResults,
    total,
    isBusy,
    tableWidth,
    sortBy,
    sortOrder,
    primaryActions,
    secondaryActions,
    keyColumn,
    avatarUrl,
    onSortByClicked,
    onSelectColumnsClicked,
    onScroll,
    getRowClassName
}) => {
    const pagedListTableContainer = useRef<HTMLDivElement | null>(null);

    const handleScroll = (value: number) => {
        if (pagedListTableContainer.current) {
            pagedListTableContainer.current.scrollLeft = value;
        }
        onScroll(value);
    };

    return (
        <Row className="my-3">
            <Col>
                <LoadingOverlay active={isBusy}>
                    <Card className="shadow">
                        <StickyScrollBar 
                            onScroll={handleScroll} 
                            scrollWidth={tableWidth} 
                            scrollLeft={pagedListTableContainer.current?.scrollLeft}
                        >
                            <div className="paged-list-table-container" ref={pagedListTableContainer}>
                                <Table hover size="sm" className="my-0" style={{ width: tableWidth }}>
                                    <thead className="bg-body border-bottom">
                                        <tr>
                                            {visibleColumns.map((column, index) => (
                                                <React.Fragment key={`${column.member}-${index}`}>
                                                    <th className={`p-0 ${ColumnRenderAlignment(column.renderAs)}`} style={{ width: `${column.width}px` }}>
                                                        {column.canSort !== false && (
                                                            <Button
                                                                variant="outline-th"
                                                                className={`w-100 border-0 rounded-0 m-0 p-2 text-dark d-flex align-items-center ${ColumnRenderAlignment(column.renderAs)}`}
                                                                onClick={() => onSortByClicked(column.member)}
                                                                title={column.renderAs === ColumnRender.deleted ? column.title : undefined}
                                                            >
                                                                {column.renderAs !== ColumnRender.deleted ? column.title : "\u00a0"}
                                                                {sortBy === column.member && sortOrder === SortOrder.ascending && (
                                                                    <FontAwesomeIcon className="ps-2 text-primary" icon={faSortUp} />
                                                                )}
                                                                {sortBy === column.member && sortOrder === SortOrder.descending && (
                                                                    <FontAwesomeIcon className="ps-2 text-primary" icon={faSortDown} />
                                                                )}
                                                            </Button>
                                                        )}
                                                        {column.canSort === false && (
                                                            <span className="p-2 d-inline-block" title={column.renderAs === ColumnRender.deleted ? column.title : undefined}>
                                                                {column.renderAs !== ColumnRender.deleted ? column.title : "\u00a0"}
                                                            </span>
                                                        )}
                                                    </th>
                                                </React.Fragment>
                                            ))}
                                            <th className="text-end p-0 align-content-center" colSpan={secondaryActions.length > 0 ? 2 : 1}>
                                                <Button
                                                    variant="outline-dark"
                                                    className="border-0 rounded-0 m-0 px-1 text-dark"
                                                    onClick={onSelectColumnsClicked}
                                                    title="Select columns"
                                                >
                                                    <FontAwesomeIcon icon={faColumns} className="px-2 height-1em" />
                                                </Button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!hasLoadError && results !== undefined &&
                                            results.map((item) => (
                                                <tr key={JSON.stringify(item)} className={getRowClassName(item)}>
                                                    {visibleColumns.map((column, index) => (
                                                        <React.Fragment key={`${column.member}-${index}`}>
                                                            <ValueRenderer item={item} column={column} avatarUrl={avatarUrl} />
                                                        </React.Fragment>
                                                    ))}
                                                    <td className={`text-end ps-2 primary-action-column ${getRowClassName(item)}`}>
                                                        {primaryActions.map((action) => (
                                                            <Button 
                                                                key={action.text} 
                                                                onClick={() => action.onClick(item)} 
                                                                className={`mr-10 ${action.secondaryBtn ? 'btn-secondary' : 'btn-primary'}`}
                                                            >
                                                                {action.icon && <FontAwesomeIcon fixedWidth={true} icon={action.icon} className="me-2" />}
                                                                {action.text}
                                                            </Button>
                                                        ))}
                                                    </td>
                                                    {secondaryActions.length !== 0 && (
                                                        <td className={`w-3em text-end ${primaryActions.length ? "ps-0" : "ps-2"} secondary-action-column ${getRowClassName(item)}`}>
                                                            <Dropdown align="end" className="position-static">
                                                                <Dropdown.Toggle variant="outline-secondary" id={`item-dropdown-${item[keyColumn]}`}></Dropdown.Toggle>
                                                                {ReactDOM.createPortal(
                                                                    <Dropdown.Menu className="shadow">
                                                                        {secondaryActions.map(
                                                                            (action) =>
                                                                                (!action.availableMember || item[action.availableMember] === (!action.availableFliped)) && (
                                                                                    <React.Fragment key={action.text}>
                                                                                        {action.startGroup === true && <Dropdown.Divider />}
                                                                                        <Dropdown.Item onClick={() => action.onClick(item)}>
                                                                                            {action.icon && <FontAwesomeIcon fixedWidth={true} icon={action.icon} className="me-2" />}
                                                                                            {action.text}
                                                                                        </Dropdown.Item>
                                                                                    </React.Fragment>
                                                                                )
                                                                        )}
                                                                    </Dropdown.Menu>,
                                                                    document.body
                                                                )}
                                                            </Dropdown>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        {!hasLoadError && hasResults && total === 0 && (
                                            <tr>
                                                <td colSpan={visibleColumns.length + 1} className="text-center">
                                                    No results to show
                                                </td>
                                            </tr>
                                        )}
                                        {!hasLoadError && !hasResults && (
                                            <tr>
                                                <td colSpan={visibleColumns.length + 1} className="height-6em"></td>
                                            </tr>
                                        )}
                                        {hasLoadError && (
                                            <tr>
                                                <td colSpan={visibleColumns.length + 1} className="text-center">
                                                    <Alert variant="danger">
                                                        <span className="fw-bold">Could not load results.</span> {`${loadErrorMessage}`}
                                                    </Alert>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </StickyScrollBar>
                    </Card>
                </LoadingOverlay>
            </Col>
        </Row>
    );
};
